import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthContext';

/**
 * Sign-up page mounted at /signup.
 *
 * On success, navigates to /confirm with the email pre-filled in
 * location state so the user can paste the verification code Cognito
 * sent them.
 */
export const SignupPage: React.FC = () => {
  const { signup, isSigningUp, signupError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const emailInvalid = submitted && email.trim() === '';
  const passwordInvalid = submitted && password === '';
  const passwordTooShort = submitted && password.length > 0 && password.length < 12;
  const passwordMismatch = submitted && password !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (
      email.trim() === '' ||
      password === '' ||
      password.length < 12 ||
      password !== confirmPassword
    ) return;

    try {
      await signup(email.trim(), password);
      navigate('/confirm', { replace: true, state: { email: email.trim() } });
    } catch {
      /* signupError surfaces below via context */
    }
  };

  return (
    <div
      data-testid="signup-page"
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white border rounded-lg shadow-sm p-6 space-y-4"
        aria-label="Create your WebPortal account"
        noValidate
      >
        <header>
          <h1 className="text-lg font-semibold">Create an account</h1>
          <p className="text-sm text-gray-600">
            We'll email you a verification code to activate it.
          </p>
        </header>

        <div className="space-y-1">
          <label htmlFor="email" className="block text-sm font-medium">Email</label>
          <input
            id="email" name="email" type="email" autoComplete="email" required
            value={email} onChange={(e) => setEmail(e.target.value)}
            aria-invalid={emailInvalid || undefined}
            disabled={isSigningUp}
            className="block w-full border rounded px-2 py-1 text-sm"
          />
          {emailInvalid && <p className="text-xs text-red-600">Email is required</p>}
        </div>

        <div className="space-y-1">
          <label htmlFor="password" className="block text-sm font-medium">Password</label>
          <input
            id="password" name="password" type="password" autoComplete="new-password" required
            value={password} onChange={(e) => setPassword(e.target.value)}
            aria-invalid={(passwordInvalid || passwordTooShort) || undefined}
            disabled={isSigningUp}
            className="block w-full border rounded px-2 py-1 text-sm"
          />
          {passwordInvalid && <p className="text-xs text-red-600">Password is required</p>}
          {passwordTooShort && (
            <p className="text-xs text-red-600">Password must be at least 12 characters</p>
          )}
          {!passwordInvalid && !passwordTooShort && (
            <p className="text-xs text-gray-500">
              Min 12 characters with lowercase, uppercase and a digit.
            </p>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="confirm-password" className="block text-sm font-medium">
            Confirm password
          </label>
          <input
            id="confirm-password" name="confirm-password" type="password"
            autoComplete="new-password" required
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
            aria-invalid={passwordMismatch || undefined}
            disabled={isSigningUp}
            className="block w-full border rounded px-2 py-1 text-sm"
          />
          {passwordMismatch && <p className="text-xs text-red-600">Passwords don't match</p>}
        </div>

        {signupError && (
          <div role="alert" className="text-sm text-red-600">{signupError}</div>
        )}

        <button
          type="submit"
          disabled={isSigningUp}
          className="w-full bg-blue-600 text-white text-sm py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSigningUp ? 'Creating account…' : 'Create account'}
        </button>

        <p className="text-xs text-gray-600 text-center pt-2 border-t">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-600 hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
};

export default SignupPage;
